import {
  cutPolygon,
  GEOMETRY_EPSILON,
  pointInPolygon,
  type CutResult,
} from '../../geometry/polygons';
import type { Line, Point, Polygon, Ring } from '../../geometry/types';

function segmentIntersectionParameter(line: Line, first: Point, second: Point): number | null {
  const lineX = line.end.x - line.start.x;
  const lineY = line.end.y - line.start.y;
  const edgeX = second.x - first.x;
  const edgeY = second.y - first.y;
  const denominator = lineX * edgeY - lineY * edgeX;
  if (Math.abs(denominator) <= GEOMETRY_EPSILON) return null;
  const offsetX = first.x - line.start.x;
  const offsetY = first.y - line.start.y;
  const lineParameter = (offsetX * edgeY - offsetY * edgeX) / denominator;
  const edgeParameter = (offsetX * lineY - offsetY * lineX) / denominator;
  if (
    lineParameter < -GEOMETRY_EPSILON ||
    lineParameter > 1 + GEOMETRY_EPSILON ||
    edgeParameter < -GEOMETRY_EPSILON ||
    edgeParameter > 1 + GEOMETRY_EPSILON
  ) {
    return null;
  }
  return Math.max(0, Math.min(1, lineParameter));
}

function ringIntersectionParameters(line: Line, ring: Ring): number[] {
  const parameters: number[] = [];
  for (let index = 0; index < ring.length; index += 1) {
    const parameter = segmentIntersectionParameter(
      line,
      ring[index],
      ring[(index + 1) % ring.length],
    );
    if (
      parameter !== null &&
      !parameters.some((candidate) => Math.abs(candidate - parameter) <= GEOMETRY_EPSILON)
    ) {
      parameters.push(parameter);
    }
  }
  return parameters;
}

function pointOnRing(point: Point, ring: Ring): boolean {
  for (let index = 0; index < ring.length; index += 1) {
    const first = ring[index];
    const second = ring[(index + 1) % ring.length];
    const edgeX = second.x - first.x;
    const edgeY = second.y - first.y;
    const lengthSquared = edgeX * edgeX + edgeY * edgeY;
    if (lengthSquared <= GEOMETRY_EPSILON) continue;
    const parameter = Math.max(
      0,
      Math.min(1, ((point.x - first.x) * edgeX + (point.y - first.y) * edgeY) / lengthSquared),
    );
    const nearestX = first.x + edgeX * parameter;
    const nearestY = first.y + edgeY * parameter;
    if (Math.hypot(point.x - nearestX, point.y - nearestY) <= GEOMETRY_EPSILON) return true;
  }
  return false;
}

function pointStrictlyInsidePolygon(point: Point, polygon: Polygon): boolean {
  if (pointOnRing(point, polygon.outer)) return false;
  if (polygon.holes.some((hole) => pointOnRing(point, hole))) return false;
  return pointInPolygon(point, polygon);
}

export function pointIsOutsidePolygon(point: Point, polygon: Polygon): boolean {
  if (pointOnRing(point, polygon.outer)) return false;
  if (polygon.holes.some((hole) => pointOnRing(point, hole))) return false;
  return !pointInPolygon(point, polygon);
}

export function segmentPassesThroughPolygon(line: Line, polygon: Polygon): boolean {
  const parameters = [
    0,
    ...ringIntersectionParameters(line, polygon.outer),
    ...polygon.holes.flatMap((hole) => ringIntersectionParameters(line, hole)),
    1,
  ].sort((first, second) => first - second);
  for (let index = 0; index < parameters.length - 1; index += 1) {
    const start = parameters[index];
    const end = parameters[index + 1];
    if (end - start <= GEOMETRY_EPSILON) continue;
    const midpoint = (start + end) / 2;
    if (
      pointStrictlyInsidePolygon(
        {
          x: line.start.x + (line.end.x - line.start.x) * midpoint,
          y: line.start.y + (line.end.y - line.start.y) * midpoint,
        },
        polygon,
      )
    ) {
      return true;
    }
  }
  return false;
}

export function ratioleDelta(error: number): number {
  const safeError = Math.max(0, error);
  const seconds = safeError <= 7 ? -3 * (7 - safeError) : Math.min(45, 3 * (safeError - 7));
  return seconds === 0 ? 0 : seconds * 1_000;
}

export type RatioleScore = {
  cut: CutResult;
  achievedPercent: number;
  error: number;
  deltaMs: number;
};

export function scoreRatiole(
  polygon: Polygon,
  targetPercent: number,
  line: Line,
): RatioleScore | null {
  const cut = cutPolygon(polygon, line);
  if (!cut) return null;
  const error = Math.abs(cut.smallerPercent - targetPercent);
  return {
    cut,
    achievedPercent: cut.smallerPercent,
    error,
    deltaMs: ratioleDelta(error),
  };
}
